/* eslint-disable tailwindcss/no-custom-classname */
'use client';
import classNames from 'classnames';
import { useRef } from 'react';
import { BaseTranslations } from '../../baseTranslations';
import { useOfferRewardModal } from '../../hooks/useOfferRewardModal';
import { ApprovalDegree, ApprovalOddsState } from '../../types/approvalOdds';
import { CmsProduct } from '../../types/cmsProduct';
import {
  GetPdpModalConfigAction,
  RenderPdpModalHero,
  VerticalType,
  PeriodGift,
} from '../../types/cmsTypes';
import { PushDataLayerFn } from '../../types/pushDataLayer';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import {
  getUrgencyPhase,
  UrgencyPhase,
} from '../../utils/helpers/calculateRewardValue';
import { getGiftLabels } from '../../utils/helpers/giftLabels';
import { hasOfferGroupLeadIn } from '../../utils/helpers/offerGroupLabels';
import { shouldUsePdpModal } from '../../utils/helpers/urlAlias';
import { LoanSearchParams } from '../../utils/searchParams/loanSearchParams';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { CarInsuranceCtaBuilderInstance } from '../CarInsurance/CarInsuranceCtaBuilder';
import { CollapsibleSection } from '../CollapsibleSection/CollapsibleSection';
import { FeCarouselV2 } from '../FeCarousel';
import { ProductDetailsModalMode } from '../ProductDetails/ProductDetailModalMode';
import { shouldRenderProductDetailModal } from '../ProductDetails/utils/shouldRenderProductDetailModal';
import RewardAsideModal from '../RewardAsideModal/RewardAsideModal';
import { RewardThumbnail } from '../RewardThumbnail';
import { RichText } from '../RichText';
import { Tag } from '../Tag';
import { TravelInsuranceCtaType } from '../TravelInsuranceCtaBuilder';
import { EligibilityOffer } from './types';

export type SectionGiftsProps = {
  vertical?: VerticalType;
  sameUrgencyPhaseOffer: UrgencyPhase | undefined;
  isVisible: boolean;
  title: JSX.Element | string;
  subtitle?: JSX.Element | string;
  toggleVisibility: () => void;
  giftsInfo: JSX.Element | string;
  gifts?: PeriodGift[];
  welcomeOffers: EligibilityOffer[];
  exclusiveOffers: EligibilityOffer[];
  giftLinkText: string;
  type: 'welcome' | 'exclusive';
  loanSearchParams: LoanSearchParams | undefined;
  carInsuranceCtaComponent: CarInsuranceCtaBuilderInstance | null;
  priceLine: React.ReactNode | null;
  product: CmsProduct;
  componentUniqueKey: string;
  ctaLinkOverride?: string;
  pushDataLayer: PushDataLayerFn;
  travelInsuranceCtaComponent?: (
    travelInsuranceCtaType: TravelInsuranceCtaType,
  ) => JSX.Element | undefined;
  approvalOdds?: ApprovalDegree | null;
  approvalOddsState?: ApprovalOddsState | null;
  /** For the marker-path ProductDetails modal; travels as a prop (CMS-1924). */
  getPdpModalConfigAction?: GetPdpModalConfigAction;
  isPreview?: boolean;
  renderPdpModalHero?: RenderPdpModalHero;
};

const SectionGifts: React.FC<SectionGiftsProps> = ({
  vertical,
  isVisible,
  title,
  subtitle,
  toggleVisibility,
  giftsInfo,
  gifts,
  giftLinkText,
  type,
  exclusiveOffers,
  welcomeOffers,
  loanSearchParams,
  carInsuranceCtaComponent,
  priceLine,
  product,
  sameUrgencyPhaseOffer,
  componentUniqueKey,
  ctaLinkOverride,
  pushDataLayer,
  travelInsuranceCtaComponent,
  approvalOdds,
  approvalOddsState,
  getPdpModalConfigAction,
  isPreview,
  renderPdpModalHero,
}) => {
  const scrollTargetRef = useRef<HTMLDivElement | null>(null);
  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);

  const [openModal] = useOfferRewardModal({
    vertical: vertical ?? 'CREDIT_CARD',
    componentUniqueKey,
  });

  return (
    <div>
      <CollapsibleSection
        id={componentUniqueKey}
        isOpen={isVisible}
        onToggle={() => toggleVisibility()}
        iconName={
          type === 'exclusive'
            ? 'featured_seasonal_and_gifts'
            : 'account_balance'
        }
        iconClassName="overflow-visible size-[18px] md:size-[22px] md:mb-0 md:mr-2"
        content={
          <>
            <div className="mhg-alpha-typography-body-2-relaxed flex flex-col items-start gap-spacing/gap/soft-12px px-padding/x py-spacing/gap/soft-12px md:flex-row md:items-center md:pl-4 lg:pl-6">
              {sameUrgencyPhaseOffer && (
                <Tag color="red" icon="alarm">
                  {sameUrgencyPhaseOffer.value}
                </Tag>
              )}
              {hasOfferGroupLeadIn(vertical) && (
                <RichText>{`${giftsInfo}`}</RichText>
              )}
            </div>
            {(gifts?.length ?? 0) > 0 && (
              <div>
                <div className="pb-padding/y-sm pl-2 pr-0 md:pl-4 lg:px-6">
                  <FeCarouselV2
                    navigation={{ showOnDesktop: true }}
                    pagination={{ showOnMobile: true }}
                  >
                    {(gifts ?? [])?.map((gift) => {
                      const giftValue = gift.gift_value;
                      const { title, subtitle } = getGiftLabels(
                        gift,
                        staticText,
                      );
                      return (
                        <button
                          key={gift.uid}
                          type="button"
                          onClick={() => {
                            openModal(product);
                          }}
                          aria-label={title}
                        >
                          <RewardThumbnail
                            data-gift-uid={gift.uid}
                            image={gift.desktop_image}
                            eyebrow={gift.offerTitle}
                            tooltip={gift.gift_title}
                            title={title}
                            subtitle={subtitle}
                            // eslint-disable-next-line tailwindcss/no-arbitrary-value
                            rewardThumbnailSizeClasses={classNames(
                              'h-32 w-[145px] md:h-36 md:w-41 xl:h-36 xl:w-[158px]',
                            )}
                            tagText={(() => {
                              const urgencyLabel = giftValue?.urgency_label;

                              if (urgencyLabel) return urgencyLabel;

                              if (sameUrgencyPhaseOffer) return undefined;

                              return getUrgencyPhase(
                                context.lang,
                                gift.startDate,
                                gift.endDate,
                              )?.value;
                            })()}
                            urgentTagText={!!giftValue?.urgency_label}
                          />
                        </button>
                      );
                    })}
                  </FeCarouselV2>
                </div>
                <div className="mhg-alpha-typography-button-2 pb-1 pl-2 md:pl-4 lg:pl-6">
                  <button
                    type="button"
                    onClick={() => openModal(product)}
                    className="cursor-pointer underline text-text/link"
                  >
                    {giftLinkText}
                  </button>
                </div>
              </div>
            )}
            {/* Same predicate as the opening branch in useOfferRewardModal — both react
                to the same rewardModal hash, so opener and renderer must pick the same
                modal (CMS-1924). No `vertical` prop on purpose: the pre-existing
                `_productDetail` modal always rendered with the generic section set, and
                passing the page vertical here flipped TI pages onto the TI branch.
                idScrollTo only on the marker path — gift clicks used to land on the
                legacy panel's rewards section, so the PDP modal opens on offers; the
                `_productDetail` path never scrolled and keeps not scrolling. */}
            {shouldRenderProductDetailModal(componentUniqueKey, product) ? (
              <ProductDetailsModalMode
                __typename="ProductDetailsComponent"
                id={componentUniqueKey}
                renderingMode="MODAL"
                product={product}
                keyFromComponent={componentUniqueKey}
                idScrollTo={
                  shouldUsePdpModal(product) ? 'section-offers' : undefined
                }
                getPdpModalConfigAction={getPdpModalConfigAction}
                isPreview={isPreview}
                renderPdpModalHero={renderPdpModalHero}
                // The card's own apply link and dataLayer sink, as the legacy panel below
                // gets them: the modal's hero and sticky footer follow the card (CMS-2043).
                ctaLinkOverride={ctaLinkOverride}
                pushDataLayer={pushDataLayer}
              />
            ) : (
              <RewardAsideModal
                welcomeOffers={welcomeOffers}
                exclusiveOffers={exclusiveOffers}
                scrollRef={type === 'welcome' ? scrollTargetRef : undefined}
                product={product}
                loanSearchParams={loanSearchParams}
                carInsuranceCtaComponent={carInsuranceCtaComponent}
                priceLine={priceLine}
                keyFromComponent={componentUniqueKey}
                ctaLinkOverride={ctaLinkOverride}
                vertical={vertical}
                pushDataLayer={pushDataLayer}
                travelInsuranceCtaComponent={travelInsuranceCtaComponent}
                approvalOdds={approvalOdds}
                approvalOddsState={approvalOddsState}
              />
            )}
          </>
        }
      >
        <span className="mhg-alpha-bullet-2 lg:mhg-alpha-bullet-1">
          {title}
        </span>
        {subtitle && (
          <span className="mhg-alpha-body-2 lg:mhg-alpha-bullet-1 hidden opacity-60 md:inline">
            {`— ${subtitle}`}
          </span>
        )}
      </CollapsibleSection>
    </div>
  );
};

export default SectionGifts;
